import React from 'react';
import classNames from 'classnames';

import BusComponent from '../BusComponent';
import MapViewer from '../MapViewer';
import ApplicationFrameType from '../../enums/ApplicationFrameType';
import AppConfig from '../../appConfig';
import MapFiltersIndicator from '../mapIndicators/MapFiltersIndicator';
import MapLogo from '../mapControls/MapLogo';
import MapControls from '../mapControls/MapControls';
import { resizeCanvas } from '../../helpers/Util';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';
import DataBrowserWrapper from '../dataBrowser/DataBrowserWrapper';
import CondensedFrameComponents from './CondensedFrameComponents';
import MapInfoBubbleSwitch from '../mapControls/MapInfoBubbleSwitch';
import ReportIframe from '../locationPanel/ReportIframe';
import PointsPanel from '../pointsPanel/PointsPanel';
import Mixpanel, { MIXPANEL_EVENTS } from '../../helpers/Mixpanel';
import MapLayersPanelContainer from '../mapLayersPanel/MapLayersPanelContainer';

class SingleMapFrame extends BusComponent {
    constructor(props, context) {
        super(props, context);

        this.state = {
            reportIframeVisible: !!this.props.frame.mapInstances[0].locationAnalysisItem,
        };
    }

    componentWillMount() {
        this.emit('APPLICATION_FRAME_TYPE_CHANGED', ApplicationFrameType.SINGLE_MAP);
        AppConfig.sentryRecordEvent('entered single map frame');
        if (this.props.displayDataBrowser) {
            this.handleDataBrowserRequest();
        }
    }

    componentDidMount() {
        this.bindGluBusEvents({
            SHOW_DATA_BROWSER: this.handleDataBrowserRequest,
            LOCATION_ANALYSIS_MODE_STARTED: this.handleShowReportIframe,
            LOCATION_ANALYSIS_MODE_EXITED: this.handleCloseReportIframe,
            COLLAPSE_ALL: this.handleCollapseAll,
        });
        this.emit('SINGLE_MAP_FRAME_MOUNTED');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    handleShowReportIframe = () => {
        this.setState({ reportIframeVisible: true });
    };

    handleCloseReportIframe = () => {
        this.setState({ reportIframeVisible: false });
    };

    handleMapImageReady = imageData => {
        const canvas = document.createElement('canvas');
        canvas.width = imageData.width;
        canvas.height = imageData.height;

        const ctx = canvas.getContext('2d');
        ctx.putImageData(imageData, 0, 0);
        ctx.globalCompositeOperation = 'copy';
        ctx.scale(1, -1);
        ctx.translate(0, -canvas.height);
        ctx.drawImage(canvas, 0, 0);

        const thumbnail = resizeCanvas(canvas).toDataURL();
        const thumbnailLarge = canvas.toDataURL('image/jpeg', 0.7);

        this.emit('UPDATE_CURRENT_FRAME_THUMBNAIL_REQUEST', {
            thumbnail,
            thumbnailLarge,
        });
    };

    handleDataBrowserRequest = () => {
        Mixpanel.emitUserEvent(MIXPANEL_EVENTS.CHANGE_DATA);
        this.setState({ showDataBrowser: true });
    };

    handleDataBrowserClose = () => {
        this.setState({ showDataBrowser: false });
    };

    handleCollapseAll = () => {
        this.setState({ showDataBrowser: false });

        // Defer to allow the data browser to close before hiding layers panel
        setTimeout(() => {
            this.bus.emit('TOGGLE_MAP_LAYERS_PANEL', { isVisible: false });
        }, 0);
    }

    renderFrameComponents() {
        const { frame, isUserStarterTier, isCondensedLayout, children } = this.props;

        const { showDataBrowser } = this.state;
        const { applicationMode } = this.context;
        const [mapInstance] = frame.mapInstances;

        if (isCondensedLayout) {
            return (
                <CondensedFrameComponents
                    mapViewer={this.map}
                    isUserStarterTier={isUserStarterTier}
                    mapInstances={frame.mapInstances}
                    activeMapIndex={0}
                    onShowDataBrowser={this.handleDataBrowserRequest}
                    applicationMode={applicationMode}
                >
                    {children}
                </CondensedFrameComponents>
            );
        }

        const componentBottomRightClasses = classNames('frame__elements__bottom-right', {
            'frame__elements__bottom-right--with-margin': this.state.reportIframeVisible,
        });

        const componentTopRightClasses = classNames('frame__elements__top-right', {
            'frame__elements__top-right--with-margin': this.state.reportIframeVisible,
        });

        return (
            <div className="frame__elements">
                {AppConfig.constants.hasFilterInSearchPanel && (
                    <PointsPanel mapInstance={mapInstance} />
                )}
                {this.map && !showDataBrowser && (
                    <MapLayersPanelContainer
                        mapInstance={mapInstance}
                        mapInstanceIndex={frame.mapInstances.indexOf(mapInstance)}
                        isUserStarterTier={isUserStarterTier}
                        onShowDataBrowser={this.handleDataBrowserRequest}
                        mapViewer={this.map}
                        tourId={HelpTourTargets.MAP_PANEL}
                    />
                )}
                <div className={componentTopRightClasses}>
                    <div className="flex-it flex-end stretch-width">
                        <MapFiltersIndicator mapInstance={mapInstance} />
                    </div>
                </div>
                <div className={componentBottomRightClasses}>
                    <div className="flex-it column center">
                        {this.props.children}
                        <MapControls mapInstance={mapInstance} />
                    </div>
                </div>
                <MapInfoBubbleSwitch mapInstance={mapInstance} />
                {AppConfig.constants.showMapFooterLogo && <MapLogo />}
            </div>
        );
    }

    render() {
        const { frame, isCondensedLayout } = this.props;

        const [mapInstance] = frame.mapInstances;

        const frameClasses = classNames('frame frame--single', {
            'frame--condensed-layout': isCondensedLayout,
        });

        return (
            <div className={frameClasses}>
                <div className="frame__map">
                    <MapViewer
                        ref={c => (this.map = c)}
                        mapInstance={mapInstance}
                        onImageReady={this.handleMapImageReady}
                        annotations
                    />
                </div>
                {this.renderFrameComponents()}
                {this.state.showDataBrowser && (
                    <DataBrowserWrapper
                        mapInstance={mapInstance}
                        onClose={this.handleDataBrowserClose}
                    />
                )}
                <ReportIframe mapInstance={mapInstance} />
            </div>
        );
    }
}

export default SingleMapFrame;
